<template>
  <transition name="lx-install-fade">
    <div v-if="show" class="lx-install" role="dialog" aria-label="Instalar app">
      <div class="lx-install__icon">
        <img src="/img/icon-192.png?v=2" alt="" />
      </div>
      <div class="lx-install__body">
        <div class="lx-install__title">Instalá Admin App en tu celular</div>
        <div class="lx-install__sub">{{ subText }}</div>
        <div class="lx-install__actions">
          <button class="lx-install__btn lx-install__btn--ghost" @click="dismiss">Más tarde</button>
          <button v-if="canInstall" class="lx-install__btn lx-install__btn--primary" @click="install">Instalar</button>
          <button v-else-if="isIOS" class="lx-install__btn lx-install__btn--primary" @click="showIOSHelp = !showIOSHelp">¿Cómo?</button>
        </div>
        <div v-if="isIOS && showIOSHelp" class="lx-install__ios">
          <div>1. Tocá <strong>Compartir</strong> <span style="font-size:14px">⬆︎</span> en Safari.</div>
          <div>2. Elegí <strong>"Agregar a pantalla de inicio"</strong>.</div>
          <div>3. Tocá <strong>Agregar</strong>.</div>
        </div>
      </div>
      <button class="lx-install__close" @click="dismiss" aria-label="Cerrar">×</button>
    </div>
  </transition>
</template>

<script>
const STORAGE_KEY = "lx_install_dismissed_at";
const REPROMPT_DAYS = 7;

export default {
  name: "InstallPrompt",
  data() {
    return {
      show: false,
      deferredPrompt: null,
      canInstall: false,
      isIOS: false,
      showIOSHelp: false,
    };
  },
  computed: {
    subText() {
      if (this.isIOS) {
        return "Para una mejor experiencia, agregala a tu pantalla de inicio.";
      }
      if (!this.canInstall) {
        return "Abrí el menú ⋮ del navegador y elegí \"Agregar a pantalla de inicio\" o \"Instalar app\".";
      }
      return "Tené acceso rápido desde tu pantalla de inicio, sin abrir el navegador.";
    },
  },
  mounted() {
    if (!this.shouldShow()) return;

    // Pick up an install event that may have fired before Vue mounted
    // (captured globally by index.html's bootstrap script).
    if (window.__pwaInstallReady && window.__pwaInstallEvent) {
      this.deferredPrompt = window.__pwaInstallEvent;
      this.canInstall = true;
      this.show = true;
    }

    // New install events (in case the page hadn't fired yet at mount time).
    this._beforeInstall = (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.canInstall = true;
      this.show = true;
    };
    window.addEventListener("beforeinstallprompt", this._beforeInstall);

    // Same listener via the custom event from index.html, in case the native
    // event already fired and only the bootstrap captured it.
    this._installReady = () => {
      if (window.__pwaInstallEvent) {
        this.deferredPrompt = window.__pwaInstallEvent;
        this.canInstall = true;
        this.show = true;
      }
    };
    document.addEventListener("pwa:install-ready", this._installReady);

    // Reset on actual install
    this._installed = () => {
      this.show = false;
      this.deferredPrompt = null;
      this.canInstall = false;
      try { localStorage.setItem(STORAGE_KEY, "installed"); } catch (e) {}
    };
    window.addEventListener("appinstalled", this._installed);

    // iOS Safari has no beforeinstallprompt — show our own banner.
    const ua = window.navigator.userAgent || "";
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    if (isIOSDevice && !isStandalone) {
      this.isIOS = true;
      // Show after a small delay so it doesn't fight with login
      setTimeout(() => { this.show = true; }, 1500);
    }

    // Android Chrome heuristic fallback: if no beforeinstallprompt has fired
    // after 8s on a mobile-eligible browser, show a manual hint banner.
    setTimeout(() => {
      if (this.show || this.deferredPrompt) return;
      const isAndroid = /Android/i.test(ua);
      if (isAndroid && !isStandalone) {
        // Show with no-canInstall — banner will explain how via menu.
        this.canInstall = false;
        this.show = true;
      }
    }, 8000);
  },
  beforeDestroy() {
    if (this._beforeInstall) window.removeEventListener("beforeinstallprompt", this._beforeInstall);
    if (this._installed) window.removeEventListener("appinstalled", this._installed);
    if (this._installReady) document.removeEventListener("pwa:install-ready", this._installReady);
  },
  methods: {
    shouldShow() {
      try {
        // Never show on the public widget routes — those are embedded on
        // third-party sites and have nothing to do with the admin panel app.
        const path = (window.location && window.location.pathname) || "";
        if (path.indexOf("/widget/") >= 0 || path.indexOf("/programming/") >= 0) {
          return false;
        }

        const isStandalone =
          (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
          window.navigator.standalone === true;
        if (isStandalone) return false;

        const isMobile = window.innerWidth < 960 ||
          /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent || "");
        if (!isMobile) return false;

        const dismissed = localStorage.getItem(STORAGE_KEY);
        if (dismissed === "installed") return false;
        if (dismissed) {
          const days = (Date.now() - parseInt(dismissed, 10)) / 86400000;
          if (days < REPROMPT_DAYS) return false;
        }
        return true;
      } catch (e) {
        return false;
      }
    },
    dismiss() {
      this.show = false;
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) {}
    },
    async install() {
      if (!this.deferredPrompt) return;
      try {
        this.deferredPrompt.prompt();
        const choice = await this.deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") {
          this.show = false;
          try { localStorage.setItem(STORAGE_KEY, "installed"); } catch (e) {}
        } else {
          this.dismiss();
        }
      } catch (e) {
        this.dismiss();
      } finally {
        this.deferredPrompt = null;
        this.canInstall = false;
      }
    },
  },
};
</script>

<style scoped>
.lx-install {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
  z-index: 2000;
  display: flex;
  gap: 12px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e1e4eb;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(15,31,61,.18), 0 4px 10px rgba(15,31,61,.06);
  font-family: 'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
  align-items: flex-start;
}
.lx-install__icon {
  width: 48px; height: 48px; flex: 0 0 48px;
  border-radius: 12px;
  overflow: hidden;
  background: #fef0f1;
  display: flex; align-items: center; justify-content: center;
}
.lx-install__icon img { width: 100%; height: 100%; object-fit: contain; }
.lx-install__body { flex: 1; min-width: 0; }
.lx-install__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f1f3d;
  margin-bottom: 3px;
  font-family: 'DM Sans','Inter',sans-serif;
}
.lx-install__sub {
  font-size: 12.5px;
  color: #626d84;
  line-height: 1.4;
}
.lx-install__actions {
  display: flex; gap: 8px;
  margin-top: 10px;
}
.lx-install__btn {
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background .15s, transform .1s;
}
.lx-install__btn:active { transform: scale(.97); }
.lx-install__btn--primary {
  background: linear-gradient(180deg, #ff3243 0%, #f8192c 60%, #d91426 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(248,25,44,.32);
}
.lx-install__btn--ghost {
  background: #f5f6fa;
  color: #626d84;
}
.lx-install__close {
  position: absolute;
  top: 6px; right: 8px;
  background: transparent;
  border: 0;
  font-size: 22px;
  color: #98a2b3;
  cursor: pointer;
  line-height: 1;
  padding: 4px 8px;
}
.lx-install__ios {
  margin-top: 10px;
  padding: 10px 12px;
  background: #f5f6fa;
  border-radius: 8px;
  font-size: 12px;
  color: #495057;
  line-height: 1.6;
}

.lx-install-fade-enter-active,
.lx-install-fade-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}
.lx-install-fade-enter,
.lx-install-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (min-width: 600px) {
  .lx-install { max-width: 460px; left: auto; right: 16px; }
}
</style>
